import type { ExerciseTemplate, Trap } from '@yuan-math/exercises';
import { fraction } from '@yuan-math/math';
import {
  choiceExercise,
  expr,
  fractionExercise,
  fractionTrap,
  leadingTerm,
  numericExercise,
  options,
  signedTerm,
  template,
  trap,
  within,
} from './authoring.ts';

/**
 * Parametric curves and polar calculus.
 *
 * Both concepts have exactly one misconception that matters, and both are
 * misconceptions of *form* rather than of arithmetic — which is why the items
 * here are built around expressions and choices as much as around numbers.
 *
 * For parametric curves it is the second derivative. dy/dx really is a quotient
 * of t-derivatives, so d²y/dx² looks as though it should be one too, and the
 * wrong version is dimensionally plausible and produces a number. The templates
 * therefore give the wrong version a value to land on, computed from the same
 * parameters, so the student who takes the shortcut gets told which shortcut
 * they took rather than merely that they are wrong.
 *
 * For polar calculus it is the factor of one half and the square. ∫r dθ is what
 * a student writes by analogy with ∫y dx, and it is not an area — it does not
 * have the units of one. Every polar item here is set on a curve whose area is
 * independently known from geometry, so the answer can be checked without
 * trusting the integral: r = 2a cos θ is a circle of radius a, and its area has
 * to come out as πa².
 *
 * `polar-rose-petal` keeps k ≥ 2. At k = 1 the "one petal" and "whole curve"
 * traps coincide, because a one-petal rose is the whole curve — and one wrong
 * number with two diagnoses is worse than one diagnosis.
 */

export const PARAMETRIC_POLAR_TEMPLATES: ExerciseTemplate[] = [
  /* -------------------------------------------------- parametric curves */
  template({
    id: 'parametric-slope',
    conceptId: 'parametric-curves',
    description: 'The slope of a parametric curve at a given value of the parameter.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(1, 4);
      const c = rng.int(1, 4);
      const d = rng.int(-6, 6);
      const at = rng.int(1, 4);
      // dx/dt must not vanish at the point asked about, or there is no slope.
      let b = rng.int(-5, 5);
      if (2 * a * at + b === 0) b += 1;

      const numerator = 3 * c * at * at + d;
      const denominator = 2 * a * at + b;
      const answer = fraction(numerator, denominator);

      const traps: Trap[] = [];
      if (numerator !== 0) {
        const inverted = fraction(denominator, numerator);
        if (!(inverted.n === answer.n && inverted.d === answer.d)) {
          traps.push(fractionTrap(inverted, 'parametric-slope-inverted'));
        }
      }

      return fractionExercise({
        prompt: `Find dy/dx at t = ${at}. Give your answer as a fraction.`,
        latex: `x = ${expr(leadingTerm(a, 't^{2}'), signedTerm(b, 't'))}, \\qquad y = ${expr(leadingTerm(c, 't^{3}'), signedTerm(d, 't'))}`,
        value: answer,
        summary: `dy/dt = ${3 * c}t² ${d < 0 ? '−' : '+'} ${Math.abs(d)} and dx/dt = ${2 * a}t ${b < 0 ? '−' : '+'} ${Math.abs(b)}. Their ratio at t = ${at} is ${numerator}/${denominator} = ${answer.n}/${answer.d}.`,
        hint: 'Differentiate each coordinate with respect to t, then divide — the t cancels and what is left is a rate per unit of x.',
        steps: [
          { label: 'Differentiate x with respect to t.', latex: `\\frac{dx}{dt} = ${2 * a}t ${b < 0 ? '-' : '+'} ${Math.abs(b)}` },
          { label: 'Differentiate y with respect to t.', latex: `\\frac{dy}{dt} = ${3 * c}t^{2} ${d < 0 ? '-' : '+'} ${Math.abs(d)}` },
          { label: 'Divide: rise over run.', latex: `\\frac{dy}{dx} = \\frac{${3 * c}t^{2} ${d < 0 ? '-' : '+'} ${Math.abs(d)}}{${2 * a}t ${b < 0 ? '-' : '+'} ${Math.abs(b)}}` },
          { label: `Substitute t = ${at}.`, latex: `\\frac{${numerator}}{${denominator}} = \\frac{${answer.n}}{${answer.d}}` },
        ],
        traps,
      });
    },
  }),

  template({
    id: 'parametric-second-derivative',
    conceptId: 'parametric-curves',
    description: 'The second derivative of a parametric curve, where the obvious shortcut fails.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const a = rng.int(1, 4);
      const b = rng.int(1, 6);
      const at = rng.int(1, 4);
      const answer = fraction(3 * b, 4 * a * a * at);
      // (d²y/dt²)/(d²x/dt²) — the shortcut, evaluated. It is never the answer:
      // equality would need 4a t² = 1, and a and t are both at least 1.
      const asRatio = fraction(3 * b * at, a);

      return fractionExercise({
        prompt: `Find the second derivative d²y/dx² at t = ${at}. Give your answer as a fraction.`,
        latex: `x = ${leadingTerm(a, 't^{2}')}, \\qquad y = ${leadingTerm(b, 't^{3}')}`,
        value: answer,
        summary: `First dy/dx = ${3 * b}t²/(${2 * a}t) = ${3 * b}t/${2 * a}. Differentiating *that* with respect to t gives ${3 * b}/${2 * a}, and dividing by dx/dt = ${2 * a}t gives ${3 * b}/(${4 * a * a}t), which at t = ${at} is ${answer.n}/${answer.d}.`,
        hint: 'Find dy/dx first and simplify it. Then differentiate that with respect to t, and divide by dx/dt — not by d²x/dt².',
        steps: [
          { label: 'The first derivative, simplified.', latex: `\\frac{dy}{dx} = \\frac{${3 * b}t^{2}}{${2 * a}t} = \\frac{${3 * b}t}{${2 * a}}` },
          { label: 'Differentiate the slope with respect to t.', latex: `\\frac{d}{dt}\\left(\\frac{dy}{dx}\\right) = \\frac{${3 * b}}{${2 * a}}` },
          { label: 'Divide by dx/dt.', latex: `\\frac{d^{2}y}{dx^{2}} = \\frac{${3 * b}/${2 * a}}{${2 * a}t} = \\frac{${3 * b}}{${4 * a * a}t}` },
          { label: `Substitute t = ${at}.`, latex: `\\frac{${answer.n}}{${answer.d}}` },
        ],
        traps: [fractionTrap(asRatio, 'parametric-second-derivative-as-ratio')],
      });
    },
  }),

  template({
    id: 'parametric-arc-length',
    conceptId: 'parametric-curves',
    description: 'Arc length of a parametric curve built so the speed simplifies.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const c = rng.int(1, 4);
      const T = rng.int(1, 5);
      const answer = ((2 * c) / 3) * ((1 + T * T) ** 1.5 - 1);
      const rootDistributed = c * T * T + ((2 * c) / 3) * T ** 3;

      return numericExercise({
        prompt: `Find the length of this curve from t = 0 to t = ${T}. Give your answer to three decimal places.`,
        latex: `x = ${leadingTerm(c, 't^{2}')}, \\qquad y = \\frac{${2 * c}}{3}t^{3}`,
        value: answer,
        tolerance: within(0.001, answer),
        summary: `dx/dt = ${2 * c}t and dy/dt = ${2 * c}t², so the speed is ${2 * c}t√(1 + t²) — the common factor is what makes this integrable. Substituting u = 1 + t² gives ${answer.toFixed(3)}.`,
        hint: 'Square both derivatives and add. A common factor should come out of the root and leave a substitution.',
        steps: [
          { label: 'Differentiate both coordinates.', latex: `\\frac{dx}{dt} = ${2 * c}t, \\qquad \\frac{dy}{dt} = ${2 * c}t^{2}` },
          { label: 'Square, add, and factor.', latex: `\\left(${2 * c}t\\right)^{2} + \\left(${2 * c}t^{2}\\right)^{2} = ${4 * c * c}t^{2}(1 + t^{2})` },
          { label: 'Take the root; t is positive on this interval.', latex: `${2 * c}t\\sqrt{1 + t^{2}}` },
          { label: 'Substitute u = 1 + t², with limits 1 and ' + `${1 + T * T}.`, latex: `L = ${c}\\int_{1}^{${1 + T * T}} \\sqrt{u}\\,du` },
          { label: 'Evaluate.', latex: `L = ${answer.toFixed(3)}` },
        ],
        traps: [trap(rootDistributed.toFixed(3), 'arc-length-root-distributed')],
      });
    },
  }),

  /* ------------------------------------------------------ polar calculus */
  template({
    id: 'polar-circle-area',
    conceptId: 'polar-calculus',
    description: 'Area enclosed by a polar circle, where the geometry gives the answer too.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(1, 9);
      const answer = Math.PI * a * a;
      const tracedTwice = 2 * answer;
      const withoutHalfSquare = 4 * a;

      return numericExercise({
        prompt: 'Find the area enclosed by this polar curve. Give your answer to three decimal places.',
        latex: `r = ${2 * a}\\cos\\theta`,
        value: answer,
        tolerance: within(0.001, answer),
        summary: `Multiplying by r gives x² + y² = ${2 * a}x, a circle of radius ${a} centred at (${a}, 0) — so the answer has to be π(${a})² = ${answer.toFixed(3)}. The wedge integral agrees: ½∫(${2 * a}cos θ)² dθ from −π/2 to π/2.`,
        hint: `Find where r returns to 0 before choosing limits, and check your answer against the circle this curve actually is.`,
        steps: [
          { label: 'Find the range that traces the curve once: r = 0 at ±π/2.', latex: `-\\frac{\\pi}{2} \\leq \\theta \\leq \\frac{\\pi}{2}` },
          { label: 'Set up the wedge integral.', latex: `A = \\frac{1}{2}\\int_{-\\pi/2}^{\\pi/2}\\left(${2 * a}\\cos\\theta\\right)^{2}d\\theta` },
          { label: 'Use the half-angle identity on the even power.', latex: `= ${2 * a * a}\\int_{-\\pi/2}^{\\pi/2}\\frac{1 + \\cos 2\\theta}{2}\\,d\\theta` },
          { label: 'The oscillating part vanishes at both ends.', latex: `A = ${a * a}\\pi` },
          { label: 'Evaluate.', latex: `A = ${answer.toFixed(3)}` },
        ],
        traps: [
          trap(tracedTwice.toFixed(3), 'polar-limits-trace-twice'),
          trap(withoutHalfSquare.toFixed(3), 'polar-area-element-wrong'),
        ],
      });
    },
  }),

  template({
    id: 'polar-rose-petal',
    conceptId: 'polar-calculus',
    description: 'Area of a single petal of a rose curve.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const a = rng.int(1, 6);
      // k ≥ 2: a one-petal rose is the whole curve, and the two traps collide.
      const k = rng.int(2, 5);
      const answer = (Math.PI * a * a) / (4 * k);
      const halfOmitted = (Math.PI * a * a) / (2 * k);
      const petals = k % 2 === 1 ? k : 2 * k;
      const wholeRose = petals * answer;

      return numericExercise({
        prompt: 'Find the area of one petal of this rose curve. Give your answer to three decimal places.',
        latex: `r = ${a === 1 ? '' : a}\\cos ${k}\\theta`,
        value: answer,
        tolerance: within(0.001, answer),
        summary: `One petal is traced as ${k}θ runs from −π/2 to π/2, so θ runs from −π/(2·${k}) to π/(2·${k}). The wedge integral over that range gives π(${a})²/(4·${k}) = ${answer.toFixed(3)}.`,
        hint: 'A petal begins and ends where r = 0. Solve cos(kθ) = 0 to find those two angles.',
        steps: [
          { label: 'A petal runs between consecutive zeros of r.', latex: `-\\frac{\\pi}{${2 * k}} \\leq \\theta \\leq \\frac{\\pi}{${2 * k}}` },
          { label: 'Set up the wedge integral.', latex: `A = \\frac{1}{2}\\int_{-\\pi/${2 * k}}^{\\pi/${2 * k}} ${a * a}\\cos^{2}${k}\\theta\\,d\\theta` },
          { label: 'Half-angle identity; the oscillating part integrates to zero.', latex: `= \\frac{${a * a}}{4}\\cdot\\frac{\\pi}{${k}}` },
          { label: 'Evaluate.', latex: `A = ${answer.toFixed(3)}` },
        ],
        traps: [
          trap(halfOmitted.toFixed(3), 'polar-area-element-wrong'),
          trap(wholeRose.toFixed(3), 'polar-limits-trace-twice'),
        ],
      });
    },
  }),

  template({
    id: 'polar-area-element',
    conceptId: 'polar-calculus',
    description: 'Choose the integral that gives a polar area.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a = rng.int(1, 9);

      return choiceExercise({
        prompt: 'Which integral gives the area enclosed by this curve?',
        latex: `r = ${2 * a}\\cos\\theta, \\qquad -\\frac{\\pi}{2} \\leq \\theta \\leq \\frac{\\pi}{2}`,
        ...options(
          [
            { latex: `\\frac{1}{2}\\int_{-\\pi/2}^{\\pi/2} \\left(${2 * a}\\cos\\theta\\right)^{2}d\\theta`, correct: true },
            { latex: `\\int_{-\\pi/2}^{\\pi/2} ${2 * a}\\cos\\theta\\,d\\theta`, misconceptionId: 'polar-area-element-wrong' },
            { latex: `\\int_{-\\pi/2}^{\\pi/2} \\left(${2 * a}\\cos\\theta\\right)^{2}d\\theta`, misconceptionId: 'polar-area-element-wrong' },
            { latex: `\\frac{1}{2}\\int_{0}^{2\\pi} \\left(${2 * a}\\cos\\theta\\right)^{2}d\\theta`, misconceptionId: 'polar-limits-trace-twice' },
          ],
          rng.shuffle,
        ),
        summary: `The piece being added up is a wedge — a sector of radius r and angle dθ, whose area is ½r² dθ. Both the square and the half come from that picture, and neither can be guessed by analogy with ∫y dx. Running θ to 2π would trace this circle twice.`,
        hint: 'What shape is the small piece of area at angle θ? It is not a rectangle.',
      });
    },
  }),

  template({
    id: 'polar-slope',
    conceptId: 'polar-calculus',
    description: 'Find the slope of a polar curve rather than its radial rate.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(1, 9);
      const b = rng.int(1, 9);

      return choiceExercise({
        prompt: 'Which expression gives dy/dx for this polar curve?',
        latex: `r = ${a === 1 ? '' : a} ${b === 1 ? '+' : `+ ${b}`}\\cos\\theta`.replace(/\s+/g, ' '),
        ...options(
          [
            {
              latex: '\\frac{\\frac{dr}{d\\theta}\\sin\\theta + r\\cos\\theta}{\\frac{dr}{d\\theta}\\cos\\theta - r\\sin\\theta}',
              correct: true,
            },
            { latex: '\\frac{dr}{d\\theta}', misconceptionId: 'polar-slope-taken-as-dr-dtheta' },
            { latex: '\\frac{r\\sin\\theta}{r\\cos\\theta}', misconceptionId: 'polar-slope-taken-as-dr-dtheta' },
            {
              latex: '\\frac{\\frac{dr}{d\\theta}\\cos\\theta - r\\sin\\theta}{\\frac{dr}{d\\theta}\\sin\\theta + r\\cos\\theta}',
              misconceptionId: 'parametric-slope-inverted',
            },
          ],
          rng.shuffle,
        ),
        summary: `θ is the parameter, so write x = r cos θ and y = r sin θ and differentiate each with the product rule. dy/dx is then (dy/dθ)/(dx/dθ). dr/dθ on its own is how fast the curve moves away from the origin as it turns — a real quantity, and not a slope.`,
        hint: 'Convert to x and y first. A polar curve is a parametric curve with θ as the parameter.',
      });
    },
  }),
];
